// ============================================================================
//#region Core Logger
// ============================================================================
/**
 * Simple logger for consistent logging across the monorepo
 */
const thlog = {
    info: (...args) => console.log('[INFO]', ...args),
    error: (...args) => console.error('[ERROR]', ...args),
    debug: (...args) => process.env.DEBUG === 'true' && console.log('[DEBUG]', ...args),
    warn: (...args) => console.warn('[WARN]', ...args),
    scenario: (prefix, ...args) => console.log(`[SCENARIO:${prefix}]`, ...args),
};
//#endregion
export default thlog;
// ============================================================================
//#region Sensitive Data Redaction
// ============================================================================
const SENSITIVE_KEYS = ['password', 'apiKey', 'api_key', 'secret'];
/**
 * Attempts to parse a string as JSON
 * @param str String to parse
 * @returns Parsed object or the original string if parsing fails
 */
function tryParseJSON(str) {
    if (typeof str !== 'string')
        return str;
    try {
        // Check if the string looks like JSON (starts with { or [)
        if ((str.startsWith('{') && str.endsWith('}')) ||
            (str.startsWith('[') && str.endsWith(']'))) {
            return JSON.parse(str);
        }
    }
    catch (e) {
        // If parsing fails, return the original string
    }
    return str;
}
/**
 * Redacts sensitive data from an object or array
 *
 * @param obj The object or array to redact
 * @param seen WeakSet to track circular references
 * @returns The redacted object with the same structure
 *
 * @example
 * ```typescript
 * const data = { email: 'user@example.com', password: 'secret123' };
 * const redacted = redactSensitiveData(data);
 * // { email: 'user@example.com', password: '[REDACTED]' }
 * ```
 */
export function redactSensitiveData(obj, seen = new WeakSet()) {
    // Early return for primitives, null, or already seen objects
    if (obj === null || typeof obj !== 'object' || seen.has(obj)) {
        return obj;
    }
    seen.add(obj);
    // Handle arrays
    if (Array.isArray(obj)) {
        return obj.map((item) => redactSensitiveData(item, seen));
    }
    // Handle objects
    const redacted = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            if (SENSITIVE_KEYS.includes(key.toLowerCase())) {
                redacted[key] = '[REDACTED]';
            }
            else {
                let value = obj[key];
                // Try to parse JSON strings (especially for 'body' field)
                if (typeof value === 'string' &&
                    (key === 'body' || key === 'rawBody')) {
                    const parsedValue = tryParseJSON(value);
                    if (parsedValue !== value) {
                        // If parsing succeeded, redact the parsed object and stringify it back
                        // We keep the result as a string to maintain the same type
                        const redactedObj = redactSensitiveData(parsedValue, new WeakSet());
                        redacted[key] = JSON.stringify(redactedObj);
                        continue; // Skip the normal assignment below
                    }
                }
                redacted[key] =
                    typeof value === 'object' && value !== null
                        ? redactSensitiveData(value, seen)
                        : value;
            }
        }
    }
    return redacted;
}
//#endregion
// ============================================================================
//#region Webhook Logging Helpers
// ============================================================================
/**
 * Logs incoming webhook with standardized format
 *
 * @param webhookSource - Webhook source (e.g., 'SHOPIFY', 'STRIPE', 'ARMBRUST')
 * @param webhookType - Webhook type/event (e.g., 'ORDER_CREATE', 'REFUND_CREATE')
 * @param body - Raw webhook body
 * @param logMetadata - Additional context to log
 *
 * @example
 * ```typescript
 * logWebhookReceived('SHOPIFY', 'ORDER_CREATE', req.body, {
 *   shopifyOrderId: req.body?.id,
 *   orderNumber: req.body?.order_number,
 * });
 * ```
 */
export function logWebhookReceived(webhookSource, webhookType, body, logMetadata) {
    const logPrefix = `[${webhookSource}_WEBHOOK][${webhookType}]`;
    const isProduction = process.env.NODE_ENV === 'prod';
    thlog.info(`${logPrefix} Webhook received`, {
        ...logMetadata,
        // Only include request body in non-production environments
        // ...(isProduction ? {} : { requestBody: redactSensitiveData(body) }),
    });
}
/**
 * Logs webhook processing errors with standardized format
 *
 * @param webhookSource - Webhook source (e.g., 'SHOPIFY', 'STRIPE', 'ARMBRUST')
 * @param webhookType - Webhook type/event (e.g., 'ORDER_CREATE', 'REFUND_CREATE')
 * @param error - The error that occurred
 * @param body - Raw webhook body (only logged in non-production)
 * @param logMetadata - Additional context to log
 *
 * @example
 * ```typescript
 * logWebhookError('SHOPIFY', 'ORDER_CREATE', err, req.body, {
 *   shopifyOrderId: req.body?.id,
 * });
 * ```
 */
export function logWebhookError(webhookSource, webhookType, error) {
    const logPrefix = `[${webhookSource}_WEBHOOK][${webhookType}]`;
    const isProduction = process.env.NODE_ENV === 'production';
    thlog.error(`${logPrefix} Error processing webhook`, {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
    });
}
/**
 * Logs that a webhook endpoint is not yet implemented
 *
 * @param webhookSource - Webhook source (e.g., 'SHOPIFY', 'STRIPE', 'ARMBRUST')
 * @param webhookType - Webhook type/event (e.g., 'ORDER_CREATE', 'REFUND_CREATE')
 *
 * @example
 * ```typescript
 * logWebhookNotImplemented('SHOPIFY', 'ORDER_CREATE');
 * ```
 */
export function logWebhookNotImplemented(webhookSource, webhookType) {
    const logPrefix = `[${webhookSource}_WEBHOOK][${webhookType}]`;
    thlog.info(`${logPrefix} Processing not yet implemented`);
}
/**
 * Logs successful webhook processing
 *
 * @param webhookSource - Webhook source (e.g., 'SHOPIFY', 'STRIPE', 'ARMBRUST')
 * @param webhookType - Webhook type/event (e.g., 'ORDER_CREATE', 'REFUND_CREATE')
 * @param logMetadata - Additional context to log
 *
 * @example
 * ```typescript
 * logWebhookSuccess('SHOPIFY', 'ORDER_CREATE', {
 *   eventId: orderEvent.eventId,
 *   orderId: orderId,
 * });
 * ```
 */
export function logWebhookSuccess(webhookSource, webhookType, logMetadata) {
    const logPrefix = `[${webhookSource}_WEBHOOK][${webhookType}]`;
    thlog.info(`${logPrefix} Webhook processed successfully`, logMetadata);
}
//#endregion
//# sourceMappingURL=thlogger.js.map